import Button from 'core/components/buttons/Button'
import useNotifications from 'core/hooks/ui/useNotifications'
import useJob, { UseJobArgs } from 'core/hooks/utils/useJob'
import { useCallback } from 'react'
import { JobStatus } from 'types/misc'
import { downloadFile } from 'utils/AppHelper'
import { useQueryClient } from '@tanstack/react-query'

type UseTaxReportJobArgs = Pick<UseJobArgs, 'blockInterval'>

const useTaxReportJob = ({
  blockInterval = false
}: UseTaxReportJobArgs = {}) => {
  const notification = useNotifications()
  const queryClient = useQueryClient()

  const onProgress = useCallback(
    (jobStatus: JobStatus) => {
      notification.loading({
        id: jobStatus.token_id as string,
        title: 'Creating a Tax Report',
        subtitle: 'It will take a few minutes to process',
        position: 'bottom-left',
        duration: 0
      })
    },
    [notification]
  )

  const onSuccess = useCallback(
    (jobStatus: JobStatus) => {
      if (jobStatus.data.file_url) {
        const teamName = jobStatus?.data?.team_name
          ? (jobStatus?.data?.team_name as string)
          : ''
        queryClient.invalidateQueries({ queryKey: ['exports', 'teams'] })
        notification.success({
          id: jobStatus.token_id as string,
          title: `Tax Report created successfully`,
          subtitle: (
            <div>
              {!!teamName && (
                <div className="mb-4">
                  Team: <span className="font-bold">{teamName}</span>
                </div>
              )}
              <div className="flex justify-end">
                <Button
                  variant="primary"
                  onClick={() => {
                    downloadFile(jobStatus.data.file_url as string)
                  }}
                >
                  Download Tax Report
                </Button>
              </div>
            </div>
          ),
          duration: 0
        })
      } else {
        notification.error({
          id: jobStatus.token_id as string,
          title: `The job doesn't have the file url`,
          subtitle: (
            <div>
              <div>
                Contact eng team and ask for Job ID: {jobStatus.token_id}
              </div>
              <div>
                {jobStatus?.data?.team_name
                  ? ` for team ${jobStatus.data.team_name}`
                  : ''}
              </div>
            </div>
          ),
          duration: 0
        })
      }
    },
    [notification, queryClient]
  )

  const onFailed = useCallback(
    (jobStatus: JobStatus) => {
      const teamName = jobStatus?.data?.team_name
        ? (jobStatus?.data?.team_name as string)
        : ''
      notification.error({
        id: jobStatus.token_id as string,
        title: `Report error${teamName ? ` for team ${teamName}` : ''}`,
        subtitle: 'There was an error while creating report',
        duration: 0
      })
    },
    [notification]
  )

  const onError = useCallback(
    (jobId: string) => {
      notification.error({
        id: jobId,
        title: 'There was an error while creating Tax Report',
        subtitle: 'Try again later.',
        duration: 0
      })
    },
    [notification]
  )

  return useJob({
    pollingJobKey: 'taxReportJobId',
    blockInterval,
    onProgress,
    onSuccess,
    onFailed,
    onError
  })
}

export default useTaxReportJob
